'use client'

import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

interface ThermalCoreProps {
	size: number
	percentChange24h?: number // Positive = green, Negative = red
	price?: number // Used to scale the core size dynamically
}

const coreVertexShader = `
  varying vec3 vNormal;
  varying vec3 vPosition;
  
  void main() {
    vNormal = normalize(normalMatrix * normal);
    vPosition = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

// Fragment shader with dynamic color uniforms
const coreFragmentShader = `
  uniform vec3 uHotCore;
  uniform vec3 uMidHeat;
  uniform vec3 uOuterHeat;
  
  varying vec3 vNormal;
  varying vec3 vPosition;
  
  void main() {
    // Create gradient from center to edges
    vec3 viewDir = normalize(cameraPosition - vPosition);
    float fresnel = 1.0 - max(dot(vNormal, viewDir), 0.0);
    
    // Blend based on fresnel (edge glow effect)
    vec3 color = mix(uHotCore, uMidHeat, fresnel * 0.7);
    color = mix(color, uOuterHeat, pow(fresnel, 2.0));
    
    // Add subtle pulsing glow
    float glow = 0.85 + 0.15 * sin(vPosition.x * 10.0 + vPosition.y * 10.0);
    color *= glow;
    
    gl_FragColor = vec4(color, 1.0);
  }
`

// Calculate color based on percent change
function getColorFromPercentChange(percentChange: number): {
	hotCore: THREE.Vector3
	midHeat: THREE.Vector3
	outerHeat: THREE.Vector3
} {
	// Clamp percent change for color intensity (-10% to +10% gives full saturation)
	// Use a lower multiplier for subtlety
	const intensity = Math.min(Math.abs(percentChange) / 10, 1) * 0.6

	if (percentChange >= 0) {
		// Positive change = Subtle green-grey tones
		return {
			hotCore: new THREE.Vector3(
				0.4 + 0.05 * intensity,
				0.45 + 0.15 * intensity,
				0.4 + 0.05 * intensity
			), // Light grey with subtle green tint
			midHeat: new THREE.Vector3(
				0.18 + 0.02 * intensity,
				0.2 + 0.08 * intensity,
				0.18 + 0.02 * intensity
			), // Medium grey with green tint
			outerHeat: new THREE.Vector3(0.04, 0.06 + 0.02 * intensity, 0.04) // Dark with slight green
		}
	} else {
		// Negative change = Subtle red-grey tones
		return {
			hotCore: new THREE.Vector3(
				0.45 + 0.15 * intensity,
				0.4 + 0.05 * intensity,
				0.4 + 0.05 * intensity
			), // Light grey with subtle red tint
			midHeat: new THREE.Vector3(
				0.2 + 0.08 * intensity,
				0.18 + 0.02 * intensity,
				0.18 + 0.02 * intensity
			), // Medium grey with red tint
			outerHeat: new THREE.Vector3(0.06 + 0.02 * intensity, 0.04, 0.04) // Dark with slight red
		}
	}
}

// Calculate size multiplier based on price (logarithmic scale)
function getSizeFromPrice(price: number, baseSize: number): number {
	if (!price || price <= 0) return baseSize

	// Use log scale for price influence on size
	// Normalize around a reference price (e.g., $50000 for BTC)
	const referencePrice = 50000
	const priceRatio = price / referencePrice

	// Scale factor: price 2x reference = 1.1x size, price 0.5x reference = 0.9x size
	const scaleFactor = 1 + Math.log10(Math.max(priceRatio, 0.1)) * 0.15

	// Clamp the scale factor to reasonable bounds (0.7 to 1.3)
	const clampedScale = Math.max(0.7, Math.min(1.3, scaleFactor))

	return baseSize * clampedScale
}

export function ThermalCore({ size, percentChange24h = 0, price = 0 }: ThermalCoreProps) {
	const meshRef = useRef<THREE.Mesh>(null)

	// Calculate dynamic size based on price
	const dynamicSize = useMemo(() => getSizeFromPrice(price, size), [price, size])

	// Create shader material with color uniforms
	const material = useMemo(() => {
		const colors = getColorFromPercentChange(percentChange24h)
		return new THREE.ShaderMaterial({
			vertexShader: coreVertexShader,
			fragmentShader: coreFragmentShader,
			side: THREE.FrontSide,
			uniforms: {
				uHotCore: { value: colors.hotCore },
				uMidHeat: { value: colors.midHeat },
				uOuterHeat: { value: colors.outerHeat }
			}
		})
	}, [percentChange24h])

	useFrame((state) => {
		if (meshRef.current) {
			// Subtle pulsing animation with dynamic size
			const scale = dynamicSize * (1 + Math.sin(state.clock.elapsedTime * 0.5) * 0.02)
			meshRef.current.scale.setScalar(scale)
		}
	})

	return (
		<mesh ref={meshRef} material={material}>
			<sphereGeometry args={[1, 64, 64]} />
		</mesh>
	)
}
